/*---------------React & Libraries-----------------*/
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { format } from "date-fns";
import { observer } from "mobx-react-lite";
import { useNavigate, useSearchParams } from "react-router-dom";

/*---------------Material UI Components-----------*/
import {
  Box,
  Typography,
  Button,
  Divider,
  Paper,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Skeleton,
  TextField,
} from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DeleteIcon from "@mui/icons-material/Delete";
import LocationPinIcon from "@mui/icons-material/LocationPin";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PaymentsIcon from "@mui/icons-material/Payments";
import FlashOnIcon from "@mui/icons-material/OfflineBolt";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import { store, useStore } from "../../app/stores/store";
import agent from "../../app/api/agent";

/*---------------Components-----------------------*/
import CouponPopup from "../../app/shared/common/CouponPopup";
import { CRM_SOURCE, CRM_USER_ID } from "../../app/shared/constants/config";

/*---------------Types---------------------------*/
interface ProductImage {
  id: number;
  path: string;
}

interface ProductVariant {
  varient_denomination_of_uom: string;
}

interface CartItem {
  productDetails: {
    name: string;
    product_images: ProductImage[];
    deliveryTime?: number;
  };
  product_varient: ProductVariant;
  quantity: number;
  item_price: number;
  inStock?: boolean;
}

const CheckoutPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [totals, setTotals] = useState<any>(null);
  const [selectedSlot, setSelectedSlot] = useState<string>("");
  const [appliedCoupon, setAppliedCoupon] = useState<any | null>(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const [localLoading, setLocalLoading] = useState(true);
  const [showSlotWarning, setShowSlotWarning] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [remainingPodLimit, setRemainingPodLimit] = useState<any>(null);
  const [notPodLimit, setNotPodLimit] = useState(false);
  const [isPodActive, setIsPodActive] = useState<boolean>(true);
  const {
    orderStore,
    cartStore,
    productStore,
    toasterStore,
    couponStore,
    commonStore,
  } = useStore();
  const { generateOrder } = orderStore;
  const [, setOrderId] = useState<Number | null>(null);
  const { getAllDeliverySlots, getCartItems, getLoadingCart, deliverySlots, cartDeliveryTime } =
    cartStore;
  const { customer, fetchCustomerDetail } = commonStore;

  // Extract URL params
  const outletId = Number(searchParams.get("outletId"));
  const customerTypeId = Number(
    searchParams.get("customerTypeId") || customer?.customerTypeId,
  );
  const urlWarehouseId =
    Number(searchParams.get("warehouseId")) || store?.commonStore?.warehouseId;

  // Get delivery type from URL params or fallback to productStore
  const urlDeliveryType = Number(searchParams.get("deliveryType"));
  const deliveryType = urlDeliveryType || productStore.deliveryType;

  // Determine if delivery type is standard (1) or quick (2)
  const isStandardDelivery = deliveryType === 1;
  const isQuickDelivery = deliveryType === 2;

  const couponId = appliedCoupon?.couponId;

  // Use cart data from store
  const cartData = cartStore.cartItems;

  const toPayAmount = totals?.finalCartTotalAmount ?? 0;
  const isLimitExceeded = useMemo(() => {
    return (
      !notPodLimit &&
      remainingPodLimit !== null &&
      toPayAmount > remainingPodLimit?.remainingLimit
    );
  }, [notPodLimit, remainingPodLimit, toPayAmount]);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchPodDetails = async () => {
      if (customer?.customerOrgId && customer.id === outletId && urlWarehouseId) {
        try {
          const limitRes = await agent.Payment.getRemainingPodLimit(
            Number(customer.customerOrgId),
            Number(urlWarehouseId),
            {
              customerTypeId: Number(customerTypeId),
              customerOrgId: Number(customer.customerOrgId),
              outletId: Number(outletId),
            }
          );
          if (limitRes?.status_code === 1) {
            setRemainingPodLimit(limitRes.data);
            if (limitRes.data?.podLimit === 0) {
              setNotPodLimit(true);
            } else {
              setNotPodLimit(false);
            }
          }
        } catch (error) {
          console.error("Error fetching POD limit:", error);
        }

        try {
          const methodsRes = await agent.Payment.getPaymentMethods(
            Number(customer.customerOrgId)
          );

          if (methodsRes?.data) {
            const hasPod = methodsRes.data.some(
              (pm: any) => pm?.paymentMethod?.paymethodKey === 'pod' && pm?.isActive === true
            );
            setIsPodActive(!!hasPod);
          }
        } catch (error) {
          console.error("Error fetching payment methods:", error);
        }
      }
    };
    fetchPodDetails();
  }, [customer?.customerOrgId, customer?.id, outletId, urlWarehouseId, customerTypeId]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    setLocalLoading(false);
  }, []);

  /*---------------Fetch Data Functions-----------------*/
  const fetchCustomer = useCallback(async () => {
    if ((!customer?.id || customer.id !== outletId) && outletId) {
      await fetchCustomerDetail(outletId);
    }
  }, [customer?.id, outletId, fetchCustomerDetail]);

  const fetchCart = useCallback(async () => {
    if (!outletId) return;
    await getCartItems({
      outletId: outletId,
      businessTypeId: customerTypeId,
      warehouseId: Number(urlWarehouseId),
      order_type: deliveryType,
    });
  }, [outletId, customerTypeId, urlWarehouseId, deliveryType, getCartItems]);

  const fetchAllCoupons = useCallback(async () => {
    if (!customerTypeId || !outletId) return;
    await couponStore.fetchApplicableCoupons(
      {
        deliveryType: deliveryType,
        warehouseId: Number(urlWarehouseId),
      },
      {
        customerTypeId: customerTypeId,
        outletId: outletId,
      },
    );
  }, [customerTypeId, outletId, deliveryType, urlWarehouseId, couponStore]);

  const fetchSlots = useCallback(async () => {
    if (isStandardDelivery) {
      await getAllDeliverySlots(
        deliveryType,
        customerTypeId,
        outletId,
        Number(customer?.customerOrgId),
      );
    }
  }, [
    isStandardDelivery,
    deliveryType,
    customerTypeId,
    outletId,
    customer?.customerOrgId,
    getAllDeliverySlots,
  ]);

  /*---------------Delivery Slot Handler-----------------*/
  const handleRemoveCoupon = async () => {
    if (!outletId || !customerTypeId || !urlWarehouseId) return;
    try {
      await getCartItems({
        outletId: outletId,
        businessTypeId: customerTypeId,
        warehouseId: Number(urlWarehouseId),
        order_type: deliveryType,
      });
      setAppliedCoupon(null);
      toasterStore.success("Coupon removed successfully!");
    } catch {
      toasterStore.error("Failed to remove coupon");
    }
  };

  /*---------------Effects-----------------*/
  useEffect(() => {
    const init = async () => {
      try {
        await Promise.all([fetchCustomer(), fetchCart(), fetchSlots()]);
      } catch (error) {
        console.error("Error initializing checkout page:", error);
      }
    };
    init();
  }, [
    fetchCustomer,
    fetchCart,
    fetchSlots,
    isStandardDelivery,
    customer?.prefferableDeliverySlot,
    customer?.prefferableDeliverySlotId,
  ]);

  useEffect(() => {
    if (couponOpen) fetchAllCoupons();
  }, [couponOpen, fetchAllCoupons]);

  useEffect(() => {
    if (isQuickDelivery) {
      setSelectedSlot("");
    }
  }, [isQuickDelivery]);

  // Sync appliedCoupon from cart API response
  useEffect(() => {
    const cartCoupon = cartData?.data?.coupon;
    const cartCouponDiscount = cartData?.data?.couponDiscountAmount;

    if (cartCoupon && cartCouponDiscount && cartCouponDiscount > 0) {
      const couponData = {
        couponCode: cartCoupon.code,
        couponId: cartCoupon.id,
        message: `Coupon ${cartCoupon.code} applied`,
        isHidden: cartCoupon.isHidden ?? false,
      };
      setAppliedCoupon(couponData);
    } else {
      setAppliedCoupon(null);
    }

    // Always set totals to cart data
    if (cartData?.data) {
      setTotals(cartData?.data);
    }
  }, [cartData?.data]);

  const handlePlaceOrder = async () => {
    // Validate that a delivery type has been selected
    if (!deliveryType) {
      toasterStore.error("Please select a delivery option");
      return;
    }

    // Validate delivery slot for standard delivery
    if (isStandardDelivery && !selectedSlot) {
      setShowSlotWarning(true);
      toasterStore.error("Please select a delivery slot");
      return;
    }

    try {
      setIsPlacingOrder(true);
      const payload: any = {
        delivery_instructions: "",
        direction_to_reach: "",
        order_delivery_type: deliveryType,
        payment_mode: 2, // COD
        slotId: isQuickDelivery ? null : Number(selectedSlot),
        use_wallet: false,
        is_pan_corner: 0,
        warehouseId: Number(urlWarehouseId),
        payMethodId: 9,
        applicationName: process.env.REACT_APP_APPLICATION_NAME || "",
        is_direct_delivery: commonStore.isDirectDelivery,
        delivery_date: commonStore.isDirectDelivery && selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null,
      };

      // Only include couponId if it's valid
      if (couponId && !isNaN(Number(couponId))) {
        payload.couponId = Number(couponId);
      }

      // Only include slotId for standard delivery
      if (isStandardDelivery && selectedSlot) {
        payload.slotId = Number(selectedSlot);
      }

      const headers = {
        customerTypeId: Number(customerTypeId),
        customerOrgId: Number(customer?.customerOrgId),
        outletId: Number(outletId),
      };

      const response = await generateOrder(payload, headers);

      // Validate response before proceeding
      if (!response) {
        toasterStore.error("Failed to place order. Please try again.");
        return;
      }

      if (response.status_code !== 1) {
        const errorMessage = response.message || "Failed to place order";
        toasterStore.error(errorMessage);
        return;
      }

      // Validate order data exists
      const orderItemResult = response?.data?.order_item_result;
      if (
        !orderItemResult ||
        orderItemResult.length === 0 ||
        !orderItemResult[0]?.order_id
      ) {
        toasterStore.error("Order was not created properly. Please try again.");
        return;
      }

      const orderIdFromResponse = orderItemResult[0].order_id;

      const crmSource = sessionStorage.getItem(CRM_SOURCE);
      const crmUserId = sessionStorage.getItem(CRM_USER_ID);
      if (crmSource === "CRM_RECENCY" && crmUserId) {
        try {
          const payload = {
            outletId: String(outletId),
            orderId: String(orderIdFromResponse),
            userId: crmUserId,
            outletName: customer?.name,
            orderValue: String(totals?.finalCartTotalAmount),
            customerTypeId: Number(customerTypeId),
            customerType: customer?.customerType?.name
          };

          agent.Orders.sendRetentionOrderPlaced(payload).catch(() => {});

          sessionStorage.removeItem(CRM_SOURCE);
          sessionStorage.removeItem(CRM_USER_ID);
        } catch (error) {
          // ignore error
        }
      }

      toasterStore.success("Order placed successfully!");
      setOrderId(orderIdFromResponse);
      navigate(`/order-detail/${orderIdFromResponse}`, { replace: true });
    } catch (error: any) {
      // Extract error message from API response if available
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to place order. Please try again.";
      toasterStore.error(errorMessage);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  /*---------------Delivery Time Grouping-----------------*/
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const formatOptions: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "short",
      year: "numeric",
    };
    return new Intl.DateTimeFormat("en-GB", formatOptions).format(date);
  };

  const { deliveryDaysMap, estimatedDateMap } = useMemo(() => {
    const deliveryDaysMap: Record<number, number> = {};
    const estimatedDateMap: Record<number, string> = {};

    if (cartDeliveryTime && Array.isArray(cartDeliveryTime)) {
      const sortedGroups = [...cartDeliveryTime].sort(
        (a, b) =>
          new Date(a.estimatedDate).getTime() -
          new Date(b.estimatedDate).getTime(),
      );

      sortedGroups.forEach((group) => {
        group.items?.forEach((item: any) => {
          deliveryDaysMap[item.productId] = group.deliveryDays;
          estimatedDateMap[item.productId] = group.estimatedDate;
        });
      });
    }
    return { deliveryDaysMap, estimatedDateMap };
  }, [cartDeliveryTime]);

  const getDeliveryDateText = (
    deliveryTime: number,
    estimatedDate?: string,
  ) => {
    if (estimatedDate) {
      const date = new Date(estimatedDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);

      const diffMs = date.getTime() - today.getTime();
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

      if (isQuickDelivery) return "Quick Delivery";
      if (diffDays === 0) return "Delivery Today";
      if (diffDays === 1) return "Delivery Tomorrow";

      return `Delivery on ${formatDate(estimatedDate)}`;
    }

    if (deliveryTime <= 1) {
      if (isQuickDelivery) return "Quick Delivery";
      return "Delivery Tomorrow";
    }
    return `Delivery in ${deliveryTime} day${deliveryTime > 1 ? "s" : ""}`;
  };

  // Filter only in-stock items
  const inStockItems =
    cartData?.data?.cart_items?.filter(
      (item: CartItem) => item?.inStock === true,
    ) || [];

  // Group in-stock items by delivery time
  const groupedCartItems = (inStockItems as CartItem[]).reduce(
    (acc: Record<number, CartItem[]>, item: CartItem) => {
      const deliveryDays = store.commonStore.isDirectDelivery ? 0 : (
        deliveryDaysMap[item?.productId] ??
        item?.productDetails?.deliveryTime ??
        0
      );
      if (!acc[deliveryDays]) acc[deliveryDays] = [];
      acc[deliveryDays].push(item);
      return acc;
    },
    {} as Record<number, CartItem[]>,
  );

  // Sort delivery times (0 first, then ascending)
  const sortedDeliveryTimes = Object.keys(groupedCartItems)
    .map(Number)
    .sort((a, b) => a - b);

  const renderCartItem = (item: CartItem, index: number) => {
    const product = item.productDetails;
    const variant = item.product_varient;
    const image = product?.product_images?.[0]?.path;

    return (
      <Box
        key={index}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={2}
        mb={1.5}
        bgcolor="#fff"
        borderRadius={1}
        sx={{
          border: "1px solid #f0f0f0",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        {/* Desktop Layout */}
        <Box
          display={{ xs: "none", sm: "flex" }}
          alignItems="center"
          gap={2}
          flex={5}
        >
          <Box
            width={50}
            height={50}
            borderRadius={1}
            overflow="hidden"
            flexShrink={0}
            bgcolor="#f4f4f4"
          >
            <img
              src={image}
              alt={product.name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
          <Box>
            <Typography fontWeight={600}>{product.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {variant.varient_denomination_of_uom}
            </Typography>
          </Box>
        </Box>

        <Box flex={2} textAlign="center" display={{ xs: "none", sm: "block" }}>
          <Typography fontWeight={500}>
            {item.quantity.toString().padStart(2, "0")}
          </Typography>
        </Box>

        <Box flex={1} textAlign="right" display={{ xs: "none", sm: "block" }}>
          <Typography fontWeight={500}>
            ₹{item?.quantity * item?.item_price}
          </Typography>
        </Box>

        {/* Mobile Layout */}
        <Box
          display={{ xs: "flex", sm: "none" }}
          flexDirection="column"
          gap={1.5}
          width="100%"
        >
          {/* Image and Product Name on top */}
          <Box display="flex" alignItems="flex-start" gap={2}>
            <Box
              width={60}
              height={60}
              borderRadius={1}
              overflow="hidden"
              flexShrink={0}
              bgcolor="#f4f4f4"
            >
              <img
                src={image}
                alt={product.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
            <Box flex={1}>
              <Typography fontWeight={600} fontSize="0.85rem">
                {product.name}
              </Typography>
            </Box>
          </Box>

          {/* Variant, Quantity and Price below */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                {variant.varient_denomination_of_uom}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                Qty: {item.quantity.toString().padStart(2, "0")}
              </Typography>
            </Box>
            <Typography fontWeight={600} fontSize="0.90rem">
              ₹{item?.quantity * item?.item_price}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", md: "row" }}
      p={{ xs: 0, md: 3 }}
      gap={3}
    >
      {/* LEFT – Products List */}
      <Box flex={{ xs: 1, md: 7 }} width={{ xs: "100%", md: "auto" }}>
        <Typography variant="h6" fontWeight={700} mb={2}>
          Checkout
        </Typography>

        <Box
          display="flex"
          px={1}
          py={1.5}
          bgcolor="#fff"
          sx={{
            display: { xs: "none", sm: "flex" },
          }}
        >
          <Box flex={5}>
            <Typography fontWeight={600}>
              Products ({inStockItems?.length})
            </Typography>
          </Box>
          <Box flex={2} textAlign="center">
            <Typography fontWeight={600}>Quantity</Typography>
          </Box>
          <Box flex={1} textAlign="right">
            <Typography fontWeight={600}>Amount</Typography>
          </Box>
        </Box>

        {/* Mobile: Show product count */}
        <Box
          display={{ xs: "block", sm: "none" }}
          px={1}
          py={1.5}
          bgcolor="#fff"
          mb={1}
        >
          <Typography fontWeight={600}>
            Products ({inStockItems?.length})
          </Typography>
        </Box>

        {/* Products Grouped by Delivery Time */}
        <Box
          sx={{
            maxHeight: { xs: "none", md: "70vh" },
            overflowY: { xs: "visible", md: "auto" },
            pr: { xs: 0, md: 1 },
          }}
        >
          {sortedDeliveryTimes.map((deliveryTime) => {
            const items = groupedCartItems[deliveryTime];
            const estimatedDate = estimatedDateMap[items[0]?.productId];
            return (
              <Paper
                key={deliveryTime}
                elevation={0}
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: "#fafafa",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                }}
              >
                {!store.commonStore.isDirectDelivery && (
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Typography fontWeight={600} color="success.main">
                      {getDeliveryDateText(deliveryTime, estimatedDate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ({items.length} {items.length > 1 ? "items" : "item"})
                    </Typography>
                  </Box>
                )}
                {items.map((item: CartItem, index: number) =>
                  renderCartItem(item, index),
                )}
              </Paper>
            );
          })}
        </Box>
      </Box>

      {/* RIGHT – Summary Area */}
      <Box
        flex={{ xs: 1, md: 3 }}
        display="flex"
        flexDirection="column"
        gap={2}
        width={{ xs: "100%", md: "auto" }}
      >
        {/* Delivery Address */}
        <Box
          p={2}
          bgcolor="#fff"
          borderRadius={2}
          boxShadow="0 1px 4px rgba(0, 0, 0, 0.05)"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography
              fontWeight={600}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <LocationPinIcon sx={{ fontSize: 14 }} />
              Outlet Details
            </Typography>
          </Box>
          {localLoading ? (
            <>
              <Skeleton variant="text" width={150} />
              <Skeleton variant="text" width="100%" />
            </>
          ) : (
            <>
              <Typography variant="subtitle2" fontWeight={600}>
                {customer?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {customer?.shippingAddressLine1},{" "}
                {customer?.shippingAddressLine2}, {customer?.shippingCity?.name}
                , {customer?.shippingPincode}, {customer?.shippingState?.name}
              </Typography>
            </>
          )}
        </Box>

        {/* Delivery Date Selection */}
        {commonStore.isDirectDelivery && (
          <Box
            p={2}
            bgcolor="#fff"
            borderRadius={2}
            boxShadow="0 1px 4px rgba(0, 0, 0, 0.05)"
          >
            <Typography
              fontWeight={600}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1.5,
              }}
            >
              <CalendarMonthIcon sx={{ fontSize: 14 }} /> Delivery Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "#fafafa",
                      },
                    },
                  },
                }}
                minDate={new Date()}
              />
            </LocalizationProvider>
          </Box>
        )}

        {/* Slot Card - Only show for Standard Delivery */}
        {isStandardDelivery && (
          <Box
            p={2}
            bgcolor="#fff"
            borderRadius={2}
            boxShadow="0 1px 4px rgba(0, 0, 0, 0.05)"
          >
            <Typography
              fontWeight={600}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <AccessTimeFilledIcon sx={{ fontSize: 14 }} /> Delivery Slot
            </Typography>
            <Box mt={{ xs: 0.5, md: 1 }}>
              {localLoading ? (
                <Skeleton variant="rectangular" width="100%" height={40} />
              ) : (cartData?.data?.cart_items ?? []).length > 0 ? (
                <FormControl fullWidth>
                  <Select
                    value={selectedSlot}
                    onChange={(e) => {
                      setSelectedSlot(e.target.value);
                      setShowSlotWarning(false);
                    }}
                    displayEmpty
                    size="small"
                    renderValue={(value) =>
                      value ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight={500}>
                            {
                              deliverySlots?.data?.find(
                                (slot) => String(slot.id) === String(value),
                              )?.fromTime
                            }{" "}
                            -{" "}
                            {
                              deliverySlots?.data?.find(
                                (slot) => String(slot.id) === String(value),
                              )?.toTime
                            }
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          Select Delivery Slot
                        </Typography>
                      )
                    }
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: {
                        sx: {
                          maxHeight: 250,
                          mt: 0.5,
                          borderRadius: 2,
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                        },
                      },
                    }}
                    sx={{
                      borderRadius: 2,
                      bgcolor: "#fafafa",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: showSlotWarning ? "#f44336" : "#e0e0e0",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    {deliverySlots?.data?.map((slot) => (
                      <MenuItem key={slot.id} value={slot.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight={500}>
                            {slot.fromTime} - {slot.toTime}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Add products to view available delivery slots.
                </Typography>
              )}
            </Box>
            {showSlotWarning && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                Please select a delivery slot to proceed
              </Typography>
            )}
          </Box>
        )}

        {/* Quick Delivery Info - Only show for Quick Delivery */}
        {isQuickDelivery && (
          <Box
            p={2}
            bgcolor="#fff"
            borderRadius={2}
            boxShadow="0 1px 4px rgba(0, 0, 0, 0.05)"
          >
            <Typography
              fontWeight={600}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <FlashOnIcon sx={{ color: "warning.main", fontSize: 16 }} /> Quick
              Delivery
            </Typography>
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              Your order will be delivered as soon as possible.
            </Typography>
          </Box>
        )}

        {/* Coupon Card */}
        <Box
          p={2}
          bgcolor="#fff"
          borderRadius={2}
          boxShadow="0 1px 4px rgba(0, 0, 0, 0.05)"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              fontWeight={600}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <LocalOfferIcon sx={{ fontSize: 14 }} /> Coupons
            </Typography>
            <Button
              size="small"
              sx={{ fontSize: 13, textTransform: "none", fontWeight: 500 }}
              onClick={() => setCouponOpen(true)}
            >
              View All
            </Button>
          </Box>

          {appliedCoupon && (totals?.couponDiscountAmount ?? 0) > 0 && (
            <Box
              mt={1.5}
              p={1.5}
              bgcolor="#e8f5e9"
              borderRadius={1}
              border="1px solid #4caf50"
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="success.main"
                >
                  {appliedCoupon.couponCode}
                </Typography>
                {appliedCoupon.isHidden && (
                  <IconButton
                    size="small"
                    onClick={handleRemoveCoupon}
                    disabled={localLoading || getLoadingCart()}
                    sx={{
                      padding: "2px",
                      color: "#F44336",
                      "&:hover": { bgcolor: "#ffebee" },
                    }}
                    title="Remove coupon"
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}
        </Box>

        <CouponPopup
          open={couponOpen}
          onClose={() => setCouponOpen(false)}
          coupons={couponStore.applicableCoupons}
          onApply={() => {
            setCouponOpen(false);
          }}
          appliedCoupon={appliedCoupon}
          onCouponUpdate={(coupon) => setAppliedCoupon(coupon)}
          loadingCoupons={couponStore.loadingApplicableCoupons}
          deliveryType={deliveryType}
        />

        {/* Total Summary */}
        <Box
          p={2}
          bgcolor="#fff"
          borderRadius={2}
          boxShadow="0 1px 4px rgba(0, 0, 0, 0.05)"
          display="flex"
          flexDirection="column"
          gap={1.5}
        >
          <Typography
            fontWeight={600}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <PaymentsIcon sx={{ fontSize: 14 }} /> Total Amount
          </Typography>

          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Sub Total (excl.tax)
            </Typography>
            <Typography variant="body2">₹{totals?.totalMRPAmount}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Tax
            </Typography>
            <Typography variant="body2">₹{totals?.totalTaxAmount}</Typography>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              Delivery Charges
            </Typography>
            {(totals?.deliveryCharges ?? 0) === 0 ? (
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{
                  color: "#2e7d32",
                  bgcolor: "#e8f5e9",
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: "0.78rem",
                  letterSpacing: 0.5,
                }}
              >
                FREE
              </Typography>
            ) : (
              <Typography variant="body2">
                ₹{totals?.deliveryCharges}
              </Typography>
            )}
          </Box>

          {/* Free delivery progress message */}
          {(totals?.deliveryCharges ?? 0) === 0 ? (
            <Box
              display="flex"
              alignItems="center"
              gap={0.75}
              px={1.5}
              py={1}
              bgcolor="#e8f5e9"
              borderRadius={1}
              border="1px solid #a5d6a7"
            >
              <Typography variant="body2" fontWeight={600} color="#2e7d32">
                FREE Delivery Applied
              </Typography>
            </Box>
          ) : (totals?.requiredAmountForFreeDelivery ?? 0) > 0 ? (
            <Box
              px={1.5}
              py={1}
              bgcolor="#fff8e1"
              borderRadius={1}
              border="1px solid #ffe082"
            >
              <Typography variant="body2" color="#e65100" fontWeight={500}>
                Add items worth{" "}
                <strong>
                  ₹{(totals?.requiredAmountForFreeDelivery ?? 0).toFixed(0)}
                </strong>{" "}
                more to get <strong>FREE Delivery</strong>
              </Typography>
            </Box>
          ) : null}
          {totals?.smallCartCharges > 0 ? (
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Small Cart Charges
              </Typography>
              <Typography variant="body2">
                ₹{totals?.smallCartCharges}
              </Typography>
            </Box>
          ) : (
            <></>
          )}
          {(totals?.couponDiscountAmount ?? 0) > 0 && appliedCoupon ? (
            <Box display="flex" justifyContent="space-between">
              <Box>
                <Typography
                  variant="body2"
                  color="success.main"
                  fontWeight={600}
                >
                  Coupon Discount
                </Typography>
              </Box>
              <Typography variant="body2" color="success.main" fontWeight={600}>
                -₹{totals?.couponDiscountAmount}
              </Typography>
            </Box>
          ) : (
            <></>
          )}

          <Divider />

          <Box display="flex" justifyContent="space-between" fontWeight={600}>
            <Typography fontWeight={700}>Total Amount</Typography>
            <Typography fontWeight={700}>
              ₹{totals?.finalCartTotalAmount}
            </Typography>
          </Box>

          {/* POD Limit Warnings */}
          {remainingPodLimit !== null && !notPodLimit && (
            <Box
              p={1.5}
              bgcolor="#f9f9f9"
              borderRadius={2}
              border="1px solid #e0e0e0"
              display="flex"
              flexDirection="column"
              gap={1}
              mt={0.5}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Remaining POD Limit:
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  ₹{remainingPodLimit?.remainingLimit?.toFixed(2)}
                </Typography>
              </Box>

              {isLimitExceeded && (
                <Typography variant="caption" color="error.main" fontWeight={600} sx={{ mt: 0.5 }}>
                  Insufficient POD limit. Remaining limit is ₹{remainingPodLimit?.remainingLimit?.toFixed(2)}, but order total is ₹{toPayAmount.toFixed(2)}. Place Order is disabled.
                </Typography>
              )}
            </Box>
          )}

          {/* POD Payment Inactive Warning */}
          {!isPodActive && (
            <Box
              p={1.5}
              bgcolor="#ffebee"
              borderRadius={2}
              border="1px solid #ffcdd2"
              display="flex"
              flexDirection="column"
              gap={1}
              mt={0.5}
            >
              <Typography variant="caption" color="error.main" fontWeight={600}>
                Pay On Delivery (POD) payment method is not active for this customer. Place Order is disabled.
              </Typography>
            </Box>
          )}

          <Box display="flex" alignItems="center" gap={1}>
            {/* <Checkbox defaultChecked sx={{ p: 0 }} /> */}
            {/* <Typography variant="body2">Use Wallet</Typography> */}
            {/* <Box ml="auto">
                <Typography variant="body2" fontWeight={600}>
                  ₹{totals?.finalCartTotalAmount}
                </Typography>
              </Box> */}
          </Box>
          <Button
            variant="contained"
            fullWidth
            onClick={handlePlaceOrder}
            disabled={inStockItems?.length === 0 || isPlacingOrder || isLimitExceeded || !isPodActive}
            sx={{
              mt: 1,
              bgcolor: "success.main",
              fontWeight: 600,
              py: 1.2,
              fontSize: "1rem",
              borderRadius: "8px",
            }}
          >
            {isPlacingOrder ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              "Place Order"
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default observer(CheckoutPage);
